import "core-js/modules/es.date.to-string.js";
import "core-js/modules/esnext.global-this.js";
import React, { useEffect } from 'react';
import { css, createGlobalStyle, ThemeProvider } from 'styled-components';
function _isPlaceholder(a) {
  return a != null && typeof a === 'object' && a['@@functional/placeholder'] === true;
}

/**
 * Optimized internal one-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */
function _curry1(fn) {
  return function f1(a) {
    if (arguments.length === 0 || _isPlaceholder(a)) {
      return f1;
    } else {
      return fn.apply(this, arguments);
    }
  };
}

/**
 * Optimized internal two-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */
function _curry2(fn) {
  return function f2(a, b) {
    switch (arguments.length) {
      case 0:
        return f2;
      case 1:
        return _isPlaceholder(a) ? f2 : _curry1(function (_b) {
          return fn(a, _b);
        });
      default:
        return _isPlaceholder(a) && _isPlaceholder(b) ? f2 : _isPlaceholder(a) ? _curry1(function (_a) {
          return fn(_a, b);
        }) : _isPlaceholder(b) ? _curry1(function (_b) {
          return fn(a, _b);
        }) : fn(a, b);
    }
  };
}
function _arrayFromIterator(iter) {
  var list = [];
  var next;
  while (!(next = iter.next()).done) {
    list.push(next.value);
  }
  return list;
}
function _includesWith(pred, x, list) {
  var idx = 0;
  var len = list.length;
  while (idx < len) {
    if (pred(x, list[idx])) {
      return true;
    }
    idx += 1;
  }
  return false;
}
function _functionName(f) {
  // String(x => x) evaluates to "x => x", so the pattern may not match.
  var match = String(f).match(/^function (\w*)/);
  return match == null ? '' : match[1];
}
function _has(prop, obj) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

// Based on https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
function _objectIs(a, b) {
  // SameValue algorithm
  if (a === b) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    return a !== 0 || 1 / a === 1 / b;
  } else {
    // Step 6.a: NaN == NaN
    return a !== a && b !== b;
  }
}
var _objectIs$1 = typeof Object.is === 'function' ? Object.is : _objectIs;
var toString = Object.prototype.toString;
var _isArguments = /*#__PURE__*/function () {
  return toString.call(arguments) === '[object Arguments]' ? function _isArguments(x) {
    return toString.call(x) === '[object Arguments]';
  } : function _isArguments(x) {
    return _has('callee', x);
  };
}();
var _isArguments$1 = _isArguments;

// cover IE < 9 keys issues
var hasEnumBug = ! /*#__PURE__*/{
  toString: null
}.propertyIsEnumerable('toString');
var nonEnumerableProps = ['constructor', 'valueOf', 'isPrototypeOf', 'toString', 'propertyIsEnumerable', 'hasOwnProperty', 'toLocaleString'];
// Safari bug
var hasArgsEnumBug = /*#__PURE__*/function () {
  return arguments.propertyIsEnumerable('length');
}();
var contains = function contains(list, item) {
  var idx = 0;
  while (idx < list.length) {
    if (list[idx] === item) {
      return true;
    }
    idx += 1;
  }
  return false;
};

/**
 * Returns a list containing the names of all the enumerable own properties of
 * the supplied object.
 * Note that the order of the output array is not guaranteed to be consistent
 * across different JS platforms.
 *
 * @func
 * @memberOf R
 * @since v0.1.0
 * @category Object
 * @sig {k: v} -> [k]
 * @param {Object} obj The object to extract properties from
 * @return {Array} An array of the object's own properties.
 * @see R.keysIn, R.values, R.toPairs
 * @example
 *
 *      R.keys({a: 1, b: 2, c: 3}); //=> ['a', 'b', 'c']
 */
var keys = typeof Object.keys === 'function' && !hasArgsEnumBug ? /*#__PURE__*/_curry1(function keys(obj) {
  return Object(obj) !== obj ? [] : Object.keys(obj);
}) : /*#__PURE__*/_curry1(function keys(obj) {
  if (Object(obj) !== obj) {
    return [];
  }
  var prop, nIdx;
  var ks = [];
  var checkArgsLength = hasArgsEnumBug && _isArguments$1(obj);
  for (prop in obj) {
    if (_has(prop, obj) && (!checkArgsLength || prop !== 'length')) {
      ks[ks.length] = prop;
    }
  }
  if (hasEnumBug) {
    nIdx = nonEnumerableProps.length - 1;
    while (nIdx >= 0) {
      prop = nonEnumerableProps[nIdx];
      if (_has(prop, obj) && !contains(ks, prop)) {
        ks[ks.length] = prop;
      }
      nIdx -= 1;
    }
  }
  return ks;
});
var keys$1 = keys;

/**
 * Gives a single-word string description of the (native) type of a value,
 * returning such answers as 'Object', 'Number', 'Array', or 'Null'. Does not
 * attempt to distinguish user Object types any further, reporting them all as
 * 'Object'.
 *
 * @func
 * @memberOf R
 * @since v0.8.0
 * @category Type
 * @sig * -> String
 * @param {*} val The value to test
 * @return {String}
 * @example
 *
 *      R.type({}); //=> "Object"
 *      R.type(1); //=> "Number"
 *      R.type(false); //=> "Boolean"
 *      R.type('s'); //=> "String"
 *      R.type(null); //=> "Null"
 *      R.type([]); //=> "Array"
 *      R.type(/[A-z]/); //=> "RegExp"
 *      R.type(() => {}); //=> "Function"
 *      R.type(async () => {}); //=> "AsyncFunction"
 *      R.type(undefined); //=> "Undefined"
 *      R.type(BigInt(123)); //=> "BigInt"
 */
var type = /*#__PURE__*/_curry1(function type(val) {
  return val === null ? 'Null' : val === undefined ? 'Undefined' : Object.prototype.toString.call(val).slice(8, -1);
});
var type$1 = type;

/**
 * private _uniqContentEquals function.
 * That function is checking equality of 2 iterator contents with 2 assumptions
 * - iterators lengths are the same
 * - iterators values are unique
 *
 * false-positive result will be returned for comparison of, e.g.
 * - [1,2,3] and [1,2,3,4]
 * - [1,1,1] and [1,2,3]
 * */

function _uniqContentEquals(aIterator, bIterator, stackA, stackB) {
  var a = _arrayFromIterator(aIterator);
  var b = _arrayFromIterator(bIterator);
  function eq(_a, _b) {
    return _equals(_a, _b, stackA.slice(), stackB.slice());
  }

  // if *a* array contains any element that is not included in *b*
  return !_includesWith(function (b, aItem) {
    return !_includesWith(eq, aItem, b);
  }, b, a);
}
function _equals(a, b, stackA, stackB) {
  if (_objectIs$1(a, b)) {
    return true;
  }
  var typeA = type$1(a);
  if (typeA !== type$1(b)) {
    return false;
  }
  if (typeof a['fantasy-land/equals'] === 'function' || typeof b['fantasy-land/equals'] === 'function') {
    return typeof a['fantasy-land/equals'] === 'function' && a['fantasy-land/equals'](b) && typeof b['fantasy-land/equals'] === 'function' && b['fantasy-land/equals'](a);
  }
  if (typeof a.equals === 'function' || typeof b.equals === 'function') {
    return typeof a.equals === 'function' && a.equals(b) && typeof b.equals === 'function' && b.equals(a);
  }
  switch (typeA) {
    case 'Arguments':
    case 'Array':
    case 'Object':
      if (typeof a.constructor === 'function' && _functionName(a.constructor) === 'Promise') {
        return a === b;
      }
      break;
    case 'Boolean':
    case 'Number':
    case 'String':
      if (!(typeof a === typeof b && _objectIs$1(a.valueOf(), b.valueOf()))) {
        return false;
      }
      break;
    case 'Date':
      if (!_objectIs$1(a.valueOf(), b.valueOf())) {
        return false;
      }
      break;
    case 'Error':
      return a.name === b.name && a.message === b.message;
    case 'RegExp':
      if (!(a.source === b.source && a.global === b.global && a.ignoreCase === b.ignoreCase && a.multiline === b.multiline && a.sticky === b.sticky && a.unicode === b.unicode)) {
        return false;
      }
      break;
  }
  var idx = stackA.length - 1;
  while (idx >= 0) {
    if (stackA[idx] === a) {
      return stackB[idx] === b;
    }
    idx -= 1;
  }
  switch (typeA) {
    case 'Map':
      if (a.size !== b.size) {
        return false;
      }
      return _uniqContentEquals(a.entries(), b.entries(), stackA.concat([a]), stackB.concat([b]));
    case 'Set':
      if (a.size !== b.size) {
        return false;
      }
      return _uniqContentEquals(a.values(), b.values(), stackA.concat([a]), stackB.concat([b]));
    case 'Arguments':
    case 'Array':
    case 'Object':
    case 'Boolean':
    case 'Number':
    case 'String':
    case 'Date':
    case 'Error':
    case 'RegExp':
    case 'Int8Array':
    case 'Uint8Array':
    case 'Uint8ClampedArray':
    case 'Int16Array':
    case 'Uint16Array':
    case 'Int32Array':
    case 'Uint32Array':
    case 'Float32Array':
    case 'Float64Array':
    case 'ArrayBuffer':
      break;
    default:
      // Values of other types are only equal if identical.
      return false;
  }
  var keysA = keys$1(a);
  if (keysA.length !== keys$1(b).length) {
    return false;
  }
  var extendedStackA = stackA.concat([a]);
  var extendedStackB = stackB.concat([b]);
  idx = keysA.length - 1;
  while (idx >= 0) {
    var key = keysA[idx];
    if (!(_has(key, b) && _equals(b[key], a[key], extendedStackA, extendedStackB))) {
      return false;
    }
    idx -= 1;
  }
  return true;
}

/**
 * Returns `true` if its arguments are equivalent, `false` otherwise. Handles
 * cyclical data structures.
 *
 * Dispatches symmetrically to the `equals` methods of both arguments, if
 * present.
 *
 * @func
 * @memberOf R
 * @since v0.15.0
 * @category Relation
 * @sig a -> b -> Boolean
 * @param {*} a
 * @param {*} b
 * @return {Boolean}
 * @example
 *
 *      R.equals(1, 1); //=> true
 *      R.equals(1, '1'); //=> false
 *      R.equals([1, 2, 3], [1, 2, 3]); //=> true
 *
 *      const a = {}; a.v = a;
 *      const b = {}; b.v = b;
 *      R.equals(a, b); //=> true
 */
var equals = /*#__PURE__*/_curry2(function equals(a, b) {
  return _equals(a, b, [], []);
});
var equals$1 = equals;

/**
 * Checks if the input value is `null` or `undefined`.
 *
 * @func
 * @memberOf R
 * @since v0.9.0
 * @category Type
 * @sig * -> Boolean
 * @param {*} x The value to test.
 * @return {Boolean} `true` if `x` is `undefined` or `null`, otherwise `false`.
 * @example
 *
 *      R.isNil(null); //=> true
 *      R.isNil(undefined); //=> true
 *      R.isNil(0); //=> false
 *      R.isNil([]); //=> false
 */
var isNil = /*#__PURE__*/_curry1(function isNil(x) {
  return x == null;
});
var isNil$1 = isNil;

/**
 * A function that returns the `!` of its argument. It will return `true` when
 * passed false-y value, and `false` when passed a truth-y one.
 *
 * @func
 * @memberOf R
 * @since v0.1.0
 * @category Logic
 * @sig * -> Boolean
 * @param {*} a any value
 * @return {Boolean} the logical inverse of passed argument.
 * @see R.complement
 * @example
 *
 *      R.not(true); //=> false
 *      R.not(false); //=> true
 *      R.not(0); //=> true
 *      R.not(1); //=> false
 */
var not = /*#__PURE__*/_curry1(function not(a) {
  return !a;
});
var not$1 = not;

/**
 * See if an object (i.e. `val`) is an instance of the supplied constructor. This
 * function will check up the inheritance chain, if any.
 * If `val` was created using `Object.create`, `R.is(Object, val) === true`.
 *
 * @func
 * @memberOf R
 * @since v0.3.0
 * @category Type
 * @sig (* -> {*}) -> a -> Boolean
 * @param {Object} ctor A constructor
 * @param {*} val The value to test
 * @return {Boolean}
 * @example
 *
 *      R.is(Object, {}); //=> true
 *      R.is(Number, 1); //=> true
 *      R.is(Object, 1); //=> false
 *      R.is(String, 's'); //=> true
 *      R.is(String, new String('')); //=> true
 *      R.is(Object, new String('')); //=> true
 *      R.is(Object, 's'); //=> false
 *      R.is(Number, {}); //=> false
 */
var is = /*#__PURE__*/_curry2(function is(Ctor, val) {
  return val instanceof Ctor || val != null && (val.constructor === Ctor || Ctor.name === 'Object' && typeof val === 'object');
});
var is$1 = is;
function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _taggedTemplateLiteral(e, t) {
  return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, {
    raw: {
      value: Object.freeze(t)
    }
  }));
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};
function createCommonjsModule(fn, basedir, module) {
  return module = {
    path: basedir,
    exports: {},
    require: function require(path, base) {
      return commonjsRequire(path, base === undefined || base === null ? module.path : base);
    }
  }, fn(module, module.exports), module.exports;
}
function commonjsRequire() {
  throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}
var react = createCommonjsModule(function (module, exports) {
  !function (t, e) {
    e(exports, React);
  }(commonjsGlobal, function (t, e) {
    function n(t) {
      return t && "object" == typeof t && "default" in t ? t : {
        "default": t
      };
    }
    var r = n(e),
      _i = function i() {
        return _i = Object.assign || function (t) {
          for (var e, n = 1, r = arguments.length; n < r; n++) for (var i in e = arguments[n]) Object.prototype.hasOwnProperty.call(e, i) && (t[i] = e[i]);
          return t;
        }, _i.apply(this, arguments);
      },
      o = function () {
        function t() {
          this.e = {};
        }
        return t.prototype.generateCallbackId = function () {
          return Math.random().toString(36).substring(7);
        }, t.prototype.on = function (t, e, n) {
          var r = this,
            i = this.e || (this.e = {}),
            o = this.generateCallbackId(),
            a = {
              id: o,
              fn: e,
              ctx: n
            };
          (i[t] || (i[t] = [])).push(a);
          return function () {
            r.off(t, o);
          }.bind(this);
        }, t.prototype.once = function (t, e, n) {
          var r = this,
            i = this.generateCallbackId();
          function o() {
            r.off(t, i), e.apply(n, arguments);
          }
          return o._ = e, this.on(t, o, n);
        }, t.prototype.emit = function (t) {
          for (var e = [], n = 1; n < arguments.length; n++) e[n - 1] = arguments[n];
          for (var r = ((this.e || (this.e = {}))[t] || []).slice(), i = r.length, o = 0; o < i; o++) r[o].fn.apply(r[o].ctx, e);
          return this;
        }, t.prototype.off = function (t, e, n) {
          var r = this.e || (this.e = {}),
            i = r[t],
            o = [];
          if (i && e) for (var a = 0, u = i.length; a < u; a++) ("function" == typeof e && i[a].fn !== e || "string" == typeof e && i[a].id !== e) && o.push(i[a]);
          return o.length ? r[t] = o : delete r[t], this;
        }, t;
      }(),
      a = new o(),
      u = e.createContext(null),
      l = function l(t) {
        var n = e.useRef(t);
        return n.current || (n.current = t), n.current;
      },
      s = function s(t) {
        if ("string" == typeof t) return [t];
        if ("object" == typeof t && t.hasOwnProperty("length")) return t;
        throw new Error("Flagsmith: please supply an array of strings or a single string of flag keys to useFlags");
      },
      f = function f(t, e, n) {
        return void 0 === n && (n = []), e.map(function (e) {
          return "".concat(t.getValue(e)).concat(t.hasFeature(e));
        }).concat(n.map(function (e) {
          return "".concat(t.getTrait(e));
        })).join(",");
      };
    t.FlagsmithContext = u, t.FlagsmithProvider = function (t) {
      var n = t.flagsmith,
        o = t.options,
        l = t.serverState,
        s = t.children,
        f = e.useRef(!0);
      return n && !(null == n ? void 0 : n._trigger) && (n._trigger = function () {
        n.log("React - trigger event received"), a.emit("event");
      }), n && !(null == n ? void 0 : n._triggerLoadingState) && (n._triggerLoadingState = function () {
        a.emit("loading_event");
      }), l && !n.initialised && n.setState(l), f.current && (f.current = !1, o && n.init(_i(_i({}, o), {
        state: o.state || l,
        onChange: function onChange() {
          for (var t = [], e = 0; e < arguments.length; e++) t[e] = arguments[e];
          o.onChange && o.onChange.apply(o, t);
        }
      }))), r["default"].createElement(u.Provider, {
        value: n
      }, s);
    }, t.useFlags = function (t, n) {
      void 0 === n && (n = []);
      var r = e.useRef(!0),
        i = l(s(t)),
        o = l(s(n)),
        c = e.useContext(u),
        g = e.useState(f(c, i, o)),
        d = g[0],
        h = g[1],
        p = e.useCallback(function () {
          var t = f(c, i, o);
          t !== d && (null == c || c.log("React - useFlags flags and traits have changed"), h(t));
        }, [d]),
        v = e.useRef(a.once("event", p));
      return r.current && (r.current = !1, null == c || c.log("React - Initialising event listeners")), e.useEffect(function () {
        return function () {
          var t;
          null === (t = v.current) || void 0 === t || t.call(v);
        };
      }, []), e.useMemo(function () {
        var t = {};
        return i.map(function (e) {
          t[e] = {
            enabled: c.hasFeature(e),
            value: c.getValue(e)
          };
        }).concat(null == o ? void 0 : o.map(function (e) {
          t[e] = c.getTrait(e);
        })), t;
      }, [d]);
    }, t.useFlagsmith = function () {
      var t = e.useContext(u);
      if (!t) throw new Error("useFlagsmith must be used with in a FlagsmithProvider");
      return t;
    }, t.useFlagsmithLoading = function () {
      var t = e.useContext(u),
        n = e.useState(null == t ? void 0 : t.loadingState),
        r = n[0],
        i = n[1],
        o = e.useState(!1),
        l = o[0],
        s = o[1],
        f = e.useRef(l),
        c = e.useCallback(function () {
          i(null == t ? void 0 : t.loadingState);
        }, [t]);
      return f.current || (a.on("loading_event", c), f.current = !0), e.useEffect(function () {
        return !l && (null == t ? void 0 : t.initialised) && (a.on("loading_event", c), s(!0)), function () {
          l && a.off("loading_event", c);
        };
      }, [t, l, c]), r;
    }, Object.defineProperty(t, "__esModule", {
      value: !0
    });
  });
});
var FEATURE_FLAGS = {
  design_system_custom_theme: 'design_system_custom_theme'
};
var WithFeatureFlag = function WithFeatureFlag(Component) {
  return function (props) {
    var flags = react.useFlags([FEATURE_FLAGS.design_system_custom_theme]);
    var _flags$FEATURE_FLAGS$ = flags[FEATURE_FLAGS.design_system_custom_theme],
      enabled = _flags$FEATURE_FLAGS$.enabled,
      value = _flags$FEATURE_FLAGS$.value;
    var overridePalette = enabled ? JSON.parse(value) : null;
    return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
      overridePalette: overridePalette
    }));
  };
};
var _templateObject$8;
var oldVariablesCss = css(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral(["\n  :root {\n    --blackColor: #000000;\n    --grayColorBorder: #e8e8e8;\n    --grayColorComponents: #f9f9fb;\n    --grayColorHover: #f4f4f4;\n    --grayColorDisabled: #bbbbbb;\n    --grayColorCard: #f7f7f7;\n    --grayColorText: #696158;\n    --grayColorCardDashed: #d7d9d9;\n    --grayColorLineDashed: #bababa;\n    --grayColorStep: #a29d98;\n    --greenColor: #00a499;\n    --greenColorBalance: #00ae99;\n    --greenColorHover: #059f8d;\n    --greenColorSelectCard: #eefbf9;\n    --orangeColorHover: #ea4800;\n    --orangeColorMain: #ff4f00;\n    --orangeColorMainActive: #f04c02;\n    --redColorError: #ff4646;\n    --tooltipColor: rgb(105, 97, 88);\n    --whiteColorBase: #ffffff;\n    --placeholderColor: #9d9994;\n    --yellowBoldColor: #ffcd00;\n    --colorGrayBorder: #e8e8e8;\n    --blueColor: #002d74;\n    --orangeColor200: #ff9565;\n    --orangeColor300: #ffe4d9;\n    --yellowColor: #ffd380;\n    --redColorMain: #fff2f2;\n    --beigeColorMain: #fff6eb;\n    --grayColor200: #aaa9a9;\n    --grayColor300: #ececec;\n    --grayColorBackground: #f6f6f7;\n  }\n"])));
var _templateObject$7;
var globalCss = css(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral(["\n  html {\n    font-size: 16px;\n    overflow: auto;\n  }\n\n  body {\n    font-family: 'Lexend';\n    font-size: 1rem;\n    font-weight: 400;\n  }\n\n  img {\n    max-width: 100%;\n  }\n"])));
var _templateObject$6, _templateObject2$1, _templateObject3;
var fadeIn = css(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral(["\n  .fade-in {\n    animation: fadeIn ease 1.2s;\n    -webkit-animation: fadeIn ease 1.2s;\n    -moz-animation: fadeIn ease 1.2s;\n    -o-animation: fadeIn ease 1.2s;\n    -ms-animation: fadeIn ease 1.2s;\n  }\n\n  @keyframes fadeIn {\n    0% {\n      opacity: 0;\n    }\n    100% {\n      opacity: 1;\n    }\n  }\n\n  @-moz-keyframes fadeIn {\n    0% {\n      opacity: 0;\n    }\n    100% {\n      opacity: 1;\n    }\n  }\n\n  @-webkit-keyframes fadeIn {\n    0% {\n      opacity: 0;\n    }\n    100% {\n      opacity: 1;\n    }\n  }\n\n  @-o-keyframes fadeIn {\n    0% {\n      opacity: 0;\n    }\n    100% {\n      opacity: 1;\n    }\n  }\n\n  @-ms-keyframes fadeIn {\n    0% {\n      opacity: 0;\n    }\n    100% {\n      opacity: 1;\n    }\n  }\n"])));
var fadeOut = css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  .fade-out {\n    display: none !important;\n    animation: fadeOut ease 1.2s;\n    -webkit-animation: fadeOut ease 1.2s;\n    -moz-animation: fadeOut ease 1.2s;\n    -o-animation: fadeOut ease 1.2s;\n    -ms-animation: fadeOut ease 1.2s;\n  }\n\n  @keyframes fadeOut {\n    0% {\n      opacity: 1;\n    }\n    100% {\n      opacity: 0;\n    }\n  }\n\n  @-moz-keyframes fadeOut {\n    0% {\n      opacity: 1;\n    }\n    100% {\n      opacity: 0;\n    }\n  }\n\n  @-webkit-keyframes fadeOut {\n    0% {\n      opacity: 1;\n    }\n    100% {\n      opacity: 0;\n    }\n  }\n\n  @-o-keyframes fadeOut {\n    0% {\n      opacity: 1;\n    }\n    100% {\n      opacity: 0;\n    }\n  }\n\n  @-ms-keyframes fadeOut {\n    0% {\n      opacity: 1;\n    }\n    100% {\n      opacity: 0;\n    }\n  }\n"])));
var animationsCss = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n  ", "\n"])), fadeIn, fadeOut);
var _templateObject$5;
var radiusCss = css(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n  :root {\n    --radius4: 4px;\n    --radius6: 6px;\n    --radius8: 8px;\n    --radius12: 12px;\n    --radius16: 16px;\n    --radius20: 20px;\n    --radius24: 24px;\n    --radius999: 999px;\n  }\n"])));
var generateCssVariablesFromObject = function generateCssVariablesFromObject(obj) {
  var cssVariables = '';
  var _recursiveCssVariableGenerator = function recursiveCssVariableGenerator(obj) {
    var parentKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    for (var key in obj) {
      var value = obj[key];
      if (equals$1(type$1(value), 'Object')) _recursiveCssVariableGenerator(value, "".concat(parentKey, "-").concat(key));else cssVariables += "-".concat(parentKey, "-").concat(key, ": ").concat(value, ";\n");
    }
  };
  _recursiveCssVariableGenerator(obj);
  return cssVariables;
};
var saveToLocalStorage = function saveToLocalStorage(key, value) {
  if (typeof window !== 'undefined') {
    try {
      var serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error("Error saving ".concat(key, " to localStorage"), error);
    }
  }
};
var PRIMA_THEME_NAME = 'prima';
var PAMAYO_THEME_NAME = 'pamayo';
var DEFAULT_THEME_NAME = PRIMA_THEME_NAME;
var THEME_NAMES = [PRIMA_THEME_NAME, PAMAYO_THEME_NAME];
var prima_palette = {
  Primary: {
    P50: '#ffede6',
    P75: '#ffb796',
    P100: '#ff996b',
    P200: '#ff6d2b',
    P300: '#ff4f00',
    P400: '#ca3f04',
    P500: '#9c3000'
  },
  Secondary: {
    S50: '#e6fafa',
    S75: '#bff2f2',
    S100: '#6be1e1',
    S200: '#00cccc',
    S300: '#04a6a2',
    S400: '#008485',
    S500: '#006464'
  },
  Tertiary: {
    T50: '#fffae6',
    T75: '#fff7d9',
    T100: '#ffe26b',
    T200: '#ffd62b',
    T300: '#ffcd00',
    T400: '#e8bb00',
    T500: '#8c7100'
  },
  Gray: {
    G0: '#ffffff',
    G10: '#fbfbfb',
    G20: '#f8f8f8',
    G30: '#f0f0f1',
    G40: '#e7e7e8',
    G50: '#d1d2d3',
    G60: '#c5c7c8',
    G70: '#bcbdc0',
    G80: '#b1b3b5',
    G90: '#a6a8ab',
    G100: '#9b9da0',
    G200: '#8f9295',
    G300: '#84878b',
    G400: '#7b7e82',
    G500: '#707377',
    G600: '#666a6f',
    G700: '#595d62',
    G800: '#4e5258',
    G900: '#292b2e'
  },
  Error: {
    E50: '#ffe6e6',
    E75: '#ff9696',
    E100: '#ff6b6b',
    E200: '#ff2b2b',
    E300: '#ee0000',
    E400: '#b30000',
    E500: '#6b0000'
  },
  Azul_Supplementary: {
    A50: '#f0faff',
    A75: '#bae3f7',
    A100: '#5ebded',
    A200: '#1791cf',
    A300: '#006599',
    A400: '#004568',
    A500: '#00334d'
  }
};
var pamayo_palette = {
  // falta Gray, error y azul
  Primary: {
    P50: '#f3f0fb',
    P75: '#cfbff0',
    P100: '#bba5ea',
    P200: '#9e7ee0',
    P300: '#8a64da',
    P400: '#614699',
    P500: '#543d85'
  },
  Secondary: {
    S50: '#e6fbf9',
    S75: '#99efe5',
    S100: '#6ee8da',
    S200: '#30decb',
    S300: '#05d7c0',
    S400: '#049786',
    S500: '#038375'
  },
  Tertiary: {
    T50: '#fff3e6',
    T75: '#ffcf96',
    T100: '#ffbb6b',
    T200: '#ff9e2b',
    T300: '#ff8a00',
    T400: '#b36100',
    T500: '#9c5400'
  },
  Gray: {
    G0: '#ffffff',
    G10: '#fbfbfb',
    G20: '#f8f8f8',
    G30: '#f0f0f1',
    G40: '#e7e7e8',
    G50: '#d1d2d3',
    G60: '#c5c7c8',
    G70: '#bcbdc0',
    G80: '#b1b3b5',
    G90: '#a6a8ab',
    G100: '#9b9da0',
    G200: '#8f9295',
    G300: '#84878b',
    G400: '#7b7e82',
    G500: '#707377',
    G600: '#666a6f',
    G700: '#595d62',
    G800: '#4e5258',
    G900: '#292b2e'
  },
  Error: {
    E50: '#ffe6e6',
    E75: '#ff9696',
    E100: '#ff6b6b',
    E200: '#ff2b2b',
    E300: '#ee0000',
    E400: '#b30000',
    E500: '#6b0000'
  },
  Azul_Supplementary: {
    A50: '#f0faff',
    A75: '#bae3f7',
    A100: '#5ebded',
    A200: '#1791cf',
    A300: '#006599',
    A400: '#004568',
    A500: '#00334d'
  }
};
var palettes = {
  prima: _objectSpread2({}, prima_palette),
  pamayo: _objectSpread2({}, pamayo_palette)
};
var theme_property = function theme_property(theme, overridePalette) {
  var paletteSelected = not$1(isNil$1(overridePalette)) ? overridePalette : palettes[theme];
  return {
    Shape: {
      Primary: {
        Subtle: paletteSelected.Primary.P50,
        Lighter: paletteSelected.Primary.P100,
        Gentle: paletteSelected.Primary.P200,
        Default: paletteSelected.Primary.P300,
        Darker: paletteSelected.Primary.P400,
        High_darker: paletteSelected.Primary.P500
      },
      Secondary: {
        Subtle: paletteSelected.Secondary.S50,
        Lighter: paletteSelected.Secondary.S100,
        Gentle: paletteSelected.Secondary.S200,
        Default: paletteSelected.Secondary.S300,
        Darker: paletteSelected.Secondary.S400,
        High_darker: paletteSelected.Secondary.S500
      },
      Tertiary: {
        Subtle: paletteSelected.Tertiary.T50,
        Lighter: paletteSelected.Tertiary.T100,
        Gentle: paletteSelected.Tertiary.T200,
        Default: paletteSelected.Tertiary.T300,
        Darker: paletteSelected.Tertiary.T400,
        High_darker: paletteSelected.Tertiary.T500
      },
      Error: {
        Subtle: paletteSelected.Error.E50,
        Lighter: paletteSelected.Error.E100,
        Gentle: paletteSelected.Error.E200,
        Default: paletteSelected.Error.E300,
        Darker: paletteSelected.Error.E400,
        High_darker: paletteSelected.Error.E500
      },
      Supplementary: {
        Subtle: paletteSelected.Azul_Supplementary.A50,
        Lighter: paletteSelected.Azul_Supplementary.A100,
        Gentle: paletteSelected.Azul_Supplementary.A200,
        Default: paletteSelected.Azul_Supplementary.A300,
        Darker: paletteSelected.Azul_Supplementary.A400,
        High_darker: paletteSelected.Azul_Supplementary.A500
      },
      White: {
        Subtle: paletteSelected.Gray.G10,
        Lighter: paletteSelected.Gray.G20,
        Gentle: paletteSelected.Gray.G30,
        Default: paletteSelected.Gray.G0,
        Darker: paletteSelected.Gray.G50,
        High_darker: paletteSelected.Gray.G60
      },
      Gray: {
        Subtle: paletteSelected.Gray.G100,
        Lighter: paletteSelected.Gray.G400,
        Gentle: paletteSelected.Gray.G600,
        Default: paletteSelected.Gray.G800,
        Darker: paletteSelected.Gray.G900
      },
      Disable: {
        Subtle: paletteSelected.Gray.G40,
        Default: paletteSelected.Gray.G300
      }
    },
    Background: {
      Primary: {
        Subtle: paletteSelected.Primary.P50,
        Lighter: paletteSelected.Primary.P75,
        Default: paletteSelected.Primary.P300,
        Darker: paletteSelected.Primary.P400,
        High_darker: paletteSelected.Primary.P500
      },
      Secondary: {
        Subtle: paletteSelected.Secondary.S50,
        Lighter: paletteSelected.Secondary.S75,
        Default: paletteSelected.Secondary.S300,
        Darker: paletteSelected.Secondary.S400,
        High_darker: paletteSelected.Secondary.S500
      },
      Tertiary: {
        Subtle: paletteSelected.Tertiary.T50,
        Lighter: paletteSelected.Tertiary.T75,
        Default: paletteSelected.Tertiary.T300,
        Darker: paletteSelected.Tertiary.T400,
        High_darker: paletteSelected.Tertiary.T500
      },
      Error: {
        Subtle: paletteSelected.Error.E50,
        Lighter: paletteSelected.Error.E75,
        Default: paletteSelected.Error.E300,
        Darker: paletteSelected.Error.E400,
        High_darker: paletteSelected.Error.E500
      },
      Supplementary: {
        Subtle: paletteSelected.Azul_Supplementary.A50,
        Lighter: paletteSelected.Azul_Supplementary.A75,
        Default: paletteSelected.Azul_Supplementary.A300,
        Darker: paletteSelected.Azul_Supplementary.A400,
        High_darker: paletteSelected.Azul_Supplementary.A500
      },
      White: {
        Subtle: paletteSelected.Gray.G20,
        Lighter: paletteSelected.Gray.G30,
        Default: paletteSelected.Gray.G0,
        Darker: paletteSelected.Gray.G50,
        High_darker: paletteSelected.Gray.G60
      },
      Gray: {
        Subtle: paletteSelected.Gray.G200,
        Lighter: paletteSelected.Gray.G500,
        Default: paletteSelected.Gray.G800,
        Darker: paletteSelected.Gray.G900
      },
      Disabled: {
        Subtle: paletteSelected.Gray.G40,
        Default: paletteSelected.Gray.G300
      }
    },
    Border: {
      Primary: {
        Subtle: paletteSelected.Primary.P100,
        Lighter: paletteSelected.Primary.P200,
        Default: paletteSelected.Primary.P300,
        Darker: paletteSelected.Primary.P400
      },
      Secondary: {
        Subtle: paletteSelected.Secondary.S100,
        Lighter: paletteSelected.Secondary.S200,
        Default: paletteSelected.Secondary.S300,
        Darker: paletteSelected.Secondary.S400
      },
      Tertiary: {
        Subtle: paletteSelected.Tertiary.T100,
        Lighter: paletteSelected.Tertiary.T200,
        Default: paletteSelected.Tertiary.T300,
        Darker: paletteSelected.Tertiary.T400
      },
      Error: {
        Subtle: paletteSelected.Error.E100,
        Lighter: paletteSelected.Error.E200,
        Default: paletteSelected.Error.E300,
        Darker: paletteSelected.Error.E400
      },
      Supplementary: {
        Subtle: paletteSelected.Azul_Supplementary.A100,
        Lighter: paletteSelected.Azul_Supplementary.A200,
        Default: paletteSelected.Azul_Supplementary.A300,
        Darker: paletteSelected.Azul_Supplementary.A400
      },
      White: {
        Subtle: paletteSelected.Gray.G30,
        Lighter: paletteSelected.Gray.G40,
        Default: paletteSelected.Gray.G0,
        Darker: paletteSelected.Gray.G50
      },
      Gray: {
        Subtle: paletteSelected.Gray.G300,
        Lighter: paletteSelected.Gray.G600,
        Default: paletteSelected.Gray.G800,
        Darker: paletteSelected.Gray.G900
      },
      Disabled: {
        Disable: paletteSelected.Gray.G60
      }
    },
    Text: {
      Primary: {
        Subtle: paletteSelected.Primary.P50,
        Lighter: paletteSelected.Primary.P75,
        Default: paletteSelected.Primary.P300,
        Darker: paletteSelected.Primary.P400,
        High_darker: paletteSelected.Primary.P500
      },
      Secondary: {
        Subtle: paletteSelected.Secondary.S50,
        Lighter: paletteSelected.Secondary.S75,
        Default: paletteSelected.Secondary.S300,
        Darker: paletteSelected.Secondary.S400,
        High_darker: paletteSelected.Secondary.S500
      },
      Tertiary: {
        Subtle: paletteSelected.Tertiary.T50,
        Lighter: paletteSelected.Tertiary.T75,
        Default: paletteSelected.Tertiary.T300,
        Darker: paletteSelected.Tertiary.T400,
        High_darker: paletteSelected.Tertiary.T500
      },
      White: {
        Subtle: paletteSelected.Gray.G20,
        Lighter: paletteSelected.Gray.G30,
        Default: paletteSelected.Gray.G0,
        Darker: paletteSelected.Gray.G60
      },
      Error: {
        Subtle: paletteSelected.Error.E50,
        Lighter: paletteSelected.Error.E75,
        Default: paletteSelected.Error.E300,
        Darker: paletteSelected.Error.E400
      },
      Gray: {
        Subtle: paletteSelected.Gray.G200,
        Lighter: paletteSelected.Gray.G500,
        Default: paletteSelected.Gray.G800,
        Darker: paletteSelected.Gray.G900
      },
      Disable: {
        Subtle: paletteSelected.Gray.G40,
        Default: paletteSelected.Gray.G300
      },
      Supplementary: {
        Subtle: paletteSelected.Azul_Supplementary.A50,
        Lighter: paletteSelected.Azul_Supplementary.A75,
        Default: paletteSelected.Azul_Supplementary.A300,
        Darker: paletteSelected.Azul_Supplementary.A400
      }
    },
    Palette: paletteSelected,
    Foundations: {
      Elevations: {
        elevation: {
          XS: '0px 1px 4px 0px rgba(69, 73, 78, 0.16)',
          S: '0px 16px 32px -4px rgba(69, 73, 78, 0.10), 0px 2px 4px 0px rgba(69, 73, 78, 0.04)',
          Focus: {
            Primary: '0px 0px 0px 3px rgba(255, 79, 0, 0.2)',
            Secondary: '0px 0px 0px 3px rgba(0, 204, 204, 0.2)',
            Tertiary: '0px 0px 0px 3px rgba(255, 205, 0, 0.2)',
            Error: '0px 0px 0px 3px rgba(255, 0, 0, 0.2)',
            Gray: '0px 0px 0px 3px rgba(41, 43, 46, 0.2)',
            Check: '0px 0px 0px 2px rgba(0, 204, 204, 0.2)'
          }
        }
      }
    }
  };
};
var validateThemeName = function validateThemeName(theme) {
  if (isNil$1(theme)) return DEFAULT_THEME_NAME;
  if (not$1(is$1(String, theme))) return DEFAULT_THEME_NAME;
  if (not$1(THEME_NAMES.includes(theme))) return DEFAULT_THEME_NAME;
  return theme;
};
var LS_THEME_KEY = 'ds-theme';
var LS_ASSETS_URL = 'assets-url';
var saveThemeToLS = function saveThemeToLS(theme) {
  return saveToLocalStorage(LS_THEME_KEY, theme);
};
var saveAssetsUrl = function saveAssetsUrl(url) {
  return saveToLocalStorage(LS_ASSETS_URL, url);
};
var _templateObject$4;
var colorsCss = function colorsCss(theme) {
  return css(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n  :root {\n    ", "\n  }\n"])), function () {
    return generateCssVariablesFromObject(theme.Palette);
  });
};
var _templateObject$3;
var elevationsCss = css(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n  :root {\n    ", "\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return generateCssVariablesFromObject(theme.Foundations.Elevations);
});
var _templateObject$2;
var resetCss = css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  *,\n  ::after,\n  ::before {\n    box-sizing: border-box;\n  }\n\n  * {\n    padding: 0;\n    margin: 0;\n  }\n\n  a {\n    text-decoration: none;\n    color: inherit;\n    cursor: pointer;\n  }\n\n  button {\n    background-color: transparent;\n    color: inherit;\n    border-width: 0;\n    padding: 0;\n    cursor: pointer;\n  }\n\n  figure {\n    margin: 0;\n  }\n\n  input::-moz-focus-inner {\n    border: 0;\n    padding: 0;\n    margin: 0;\n  }\n\n  dd,\n  ol,\n  ul {\n    margin: 0;\n    padding: 0;\n    list-style: none;\n  }\n\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6 {\n    margin: 0;\n    font-size: inherit;\n    font-weight: inherit;\n  }\n\n  p {\n    margin: 0;\n  }\n\n  cite {\n    font-style: normal;\n  }\n\n  fieldset {\n    border-width: 0;\n    padding: 0;\n    margin: 0;\n  }\n\n  body {\n    margin: 0;\n    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans',\n      'Droid Sans', 'Helvetica Neue', sans-serif;\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n    overflow: hidden;\n  }\n\n  code {\n    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;\n  }\n"])));
var _templateObject$1;
var normalizeCss = css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  /* normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */\n\n  /* Document\n    ========================================================================== */\n\n  /**\n  * 1. Correct the line height in all browsers.\n  * 2. Prevent adjustments of font size after orientation changes in iOS.\n  */\n\n  html {\n    line-height: 1.15; /* 1 */\n    -webkit-text-size-adjust: 100%; /* 2 */\n  }\n\n  /* Sections\n    ========================================================================== */\n\n  /**\n  * Remove the margin in all browsers.\n  */\n\n  body {\n    margin: 0;\n  }\n\n  /**\n  * Render the main element consistently in IE.\n  */\n\n  main {\n    display: block;\n  }\n\n  /**\n  * Correct the font size and margin on h1 elements within section and\n  * article contexts in Chrome, Firefox, and Safari.\n  */\n\n  h1 {\n    font-size: 2em;\n    margin: 0.67em 0;\n  }\n\n  /* Grouping content\n    ========================================================================== */\n\n  /**\n  * 1. Add the correct box sizing in Firefox.\n  * 2. Show the overflow in Edge and IE.\n  */\n\n  hr {\n    box-sizing: content-box; /* 1 */\n    height: 0; /* 1 */\n    overflow: visible; /* 2 */\n  }\n\n  /**\n  * 1. Correct the inheritance and scaling of font size in all browsers.\n  * 2. Correct the odd em font sizing in all browsers.\n  */\n\n  pre {\n    font-family: monospace, monospace; /* 1 */\n    font-size: 1em; /* 2 */\n  }\n\n  /* Text-level semantics\n    ========================================================================== */\n\n  /**\n  * Remove the gray background on active links in IE 10.\n  */\n\n  a {\n    background-color: transparent;\n  }\n\n  /**\n  * 1. Remove the bottom border in Chrome 57-\n  * 2. Add the correct text decoration in Chrome, Edge, IE, Opera, and Safari.\n  */\n\n  abbr[title] {\n    border-bottom: none; /* 1 */\n    text-decoration: underline; /* 2 */\n    text-decoration: underline dotted; /* 2 */\n  }\n\n  /**\n  * Add the correct font weight in Chrome, Edge, and Safari.\n  */\n\n  b,\n  strong {\n    font-weight: bolder;\n  }\n\n  /**\n  * 1. Correct the inheritance and scaling of font size in all browsers.\n  * 2. Correct the odd em font sizing in all browsers.\n  */\n\n  code,\n  kbd,\n  samp {\n    font-family: monospace, monospace; /* 1 */\n    font-size: 1em; /* 2 */\n  }\n\n  /**\n  * Add the correct font size in all browsers.\n  */\n\n  small {\n    font-size: 80%;\n  }\n\n  /**\n  * Prevent sub and sup elements from affecting the line height in\n  * all browsers.\n  */\n\n  sub,\n  sup {\n    font-size: 75%;\n    line-height: 0;\n    position: relative;\n    vertical-align: baseline;\n  }\n\n  sub {\n    bottom: -0.25em;\n  }\n\n  sup {\n    top: -0.5em;\n  }\n\n  /* Embedded content\n    ========================================================================== */\n\n  /**\n  * Remove the border on images inside links in IE 10.\n  */\n\n  img {\n    border-style: none;\n  }\n\n  /* Forms\n    ========================================================================== */\n\n  /**\n  * 1. Change the font styles in all browsers.\n  * 2. Remove the margin in Firefox and Safari.\n  */\n\n  button,\n  input,\n  optgroup,\n  select,\n  textarea {\n    font-family: inherit; /* 1 */\n    font-size: 100%; /* 1 */\n    line-height: 1.15; /* 1 */\n    margin: 0; /* 2 */\n  }\n\n  /**\n  * Show the overflow in IE.\n  * 1. Show the overflow in Edge.\n  */\n\n  button,\n  input {\n    /* 1 */\n    overflow: visible;\n  }\n\n  /**\n  * Remove the inheritance of text transform in Edge, Firefox, and IE.\n  * 1. Remove the inheritance of text transform in Firefox.\n  */\n\n  button,\n  select {\n    /* 1 */\n    text-transform: none;\n  }\n\n  /**\n  * Correct the inability to style clickable types in iOS and Safari.\n  */\n\n  button,\n  [type='button'],\n  [type='reset'],\n  [type='submit'] {\n    -webkit-appearance: button;\n    appearance: button;\n  }\n\n  /**\n  * Remove the inner border and padding in Firefox.\n  */\n\n  button::-moz-focus-inner,\n  [type='button']::-moz-focus-inner,\n  [type='reset']::-moz-focus-inner,\n  [type='submit']::-moz-focus-inner {\n    border-style: none;\n    padding: 0;\n  }\n\n  /**\n  * Restore the focus styles unset by the previous rule.\n  */\n\n  button:-moz-focusring,\n  [type='button']:-moz-focusring,\n  [type='reset']:-moz-focusring,\n  [type='submit']:-moz-focusring {\n    outline: 1px dotted ButtonText;\n  }\n\n  /**\n  * Correct the padding in Firefox.\n  */\n\n  fieldset {\n    padding: 0.35em 0.75em 0.625em;\n  }\n\n  /**\n  * 1. Correct the text wrapping in Edge and IE.\n  * 2. Correct the color inheritance from fieldset elements in IE.\n  * 3. Remove the padding so developers are not caught out when they zero out\n  *    fieldset elements in all browsers.\n  */\n\n  legend {\n    box-sizing: border-box; /* 1 */\n    color: inherit; /* 2 */\n    display: table; /* 1 */\n    max-width: 100%; /* 1 */\n    padding: 0; /* 3 */\n    white-space: normal; /* 1 */\n  }\n\n  /**\n  * Add the correct vertical alignment in Chrome, Firefox, and Opera.\n  */\n\n  progress {\n    vertical-align: baseline;\n  }\n\n  /**\n  * Remove the default vertical scrollbar in IE 10+.\n  */\n\n  textarea {\n    overflow: auto;\n  }\n\n  /**\n  * 1. Add the correct box sizing in IE 10.\n  * 2. Remove the padding in IE 10.\n  */\n\n  [type='checkbox'],\n  [type='radio'] {\n    box-sizing: border-box; /* 1 */\n    padding: 0; /* 2 */\n  }\n\n  /**\n  * Correct the cursor style of increment and decrement buttons in Chrome.\n  */\n\n  [type='number']::-webkit-inner-spin-button,\n  [type='number']::-webkit-outer-spin-button {\n    height: auto;\n  }\n\n  /**\n  * 1. Correct the odd appearance in Chrome and Safari.\n  * 2. Correct the outline style in Safari.\n  */\n\n  [type='search'] {\n    -webkit-appearance: textfield; /* 1 */\n    appearance: textfield; /* Add the standard property 'appearance' for compatibility */\n    outline-offset: -2px; /* 2 */\n  }\n\n  /**\n  * Remove the inner padding in Chrome and Safari on macOS.\n  */\n\n  [type='search']::-webkit-search-decoration {\n    -webkit-appearance: none;\n  }\n\n  /**\n  * 1. Correct the inability to style clickable types in iOS and Safari.\n  * 2. Change font properties to inherit in Safari.\n  */\n\n  ::-webkit-file-upload-button {\n    -webkit-appearance: button; /* 1 */\n    font: inherit; /* 2 */\n  }\n\n  /* Interactive\n    ========================================================================== */\n\n  /*\n  * Add the correct display in Edge, IE 10+, and Firefox.\n  */\n\n  details {\n    display: block;\n  }\n\n  /*\n  * Add the correct display in all browsers.\n  */\n\n  summary {\n    display: list-item;\n  }\n\n  /* Misc\n    ========================================================================== */\n\n  /**\n  * Add the correct display in IE 10+.\n  */\n\n  template {\n    display: none;\n  }\n\n  /**\n  * Add the correct display in IE 10.\n  */\n\n  [hidden] {\n    display: none;\n  }\n"])));
var _templateObject, _templateObject2;
var GlobalStyles = createGlobalStyle(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var theme = _ref.theme;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", "\n    ", "\n    ", "\n    ", "\n    ", "\n    ", "\n    ", "\n    ", "\n  "])), oldVariablesCss, normalizeCss, resetCss, globalCss, radiusCss, elevationsCss, colorsCss(theme), animationsCss);
});

/**
 * StyleCoreProvider is the main provider for consume the package.
 * Its wraps the app with ThemeProvider and GlobalStyles.
 */
var StyleCoreProvider = function StyleCoreProvider(props) {
  var children = props.children,
    theme = props.theme,
    assetsUrl = props.assetsUrl,
    _props$withFeatureFla = props.withFeatureFlag,
    withFeatureFlag = _props$withFeatureFla === void 0 ? false : _props$withFeatureFla;
  if (isNil$1(assetsUrl)) throw new Error("Set assetsUrl prop in StyleCoreProvider according to your environment (for example: assetsUrl={process.env.REACT_APP_ASSETS_URL})");
  var ThemeProviderResolved = withFeatureFlag ? WithFeatureFlag(ThemeProviderWrapper) : ThemeProviderWrapper;
  return /*#__PURE__*/React.createElement(ThemeProviderResolved, {
    theme: theme,
    assetsUrl: assetsUrl
  }, /*#__PURE__*/React.createElement(GlobalStyles, null), children);
};
var ThemeProviderWrapper = function ThemeProviderWrapper(props) {
  var children = props.children,
    theme = props.theme,
    overridePalette = props.overridePalette,
    assetsUrl = props.assetsUrl;
  var themeObject = theme_property(validateThemeName(theme), overridePalette);
  useEffect(function () {
    saveAssetsUrl(assetsUrl);
    saveThemeToLS(themeObject);
  }, [themeObject]);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: themeObject
  }, children);
};
export { StyleCoreProvider, ThemeProviderWrapper as ThemeProvider };